import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import ImageUpload from '../components/ui/ImageUpload';

const { FiArrowRight, FiArrowLeft, FiCheck, FiPlus, FiSearch } = FiIcons;

const ProfileWizard = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showCustomCompany, setShowCustomCompany] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const { user } = useAuth();
  const { saveProfile, companies, addCompany } = useData();
  const navigate = useNavigate();

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm();

  const steps = [
    'Basic Info',
    'Role & Business', 
    'Preferences',
    'Matching Criteria',
    'Goals & Summary'
  ];

  const roles = ['Developer', 'Publisher', 'Investor', 'Mentor'];
  const businessTypes = ['Indie Studio', 'AAA Studio', 'Publishing House', 'Investment Firm', 'Consulting', 'Individual'];
  const platforms = ['PC', 'Mobile', 'Console', 'VR/AR', 'Web', 'Arcade'];
  const genres = ['Action', 'Adventure', 'RPG', 'Strategy', 'Simulation', 'Puzzle', 'Horror', 'Racing', 'Sports', 'Casual'];
  const markets = ['North America', 'Europe', 'Asia', 'Global', 'Emerging Markets'];
  const needsOffers = ['Investment', 'Publishing', 'Mentorship', 'Partnership', 'Development', 'Marketing', 'Distribution', 'Technical Support'];

  // Matching criteria options
  const experienceLevels = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];
  const companySizes = ['Solo', '2-10', '11-50', '51-200', '200+'];
  const projectStages = ['Concept', 'Pre-Production', 'Production', 'Beta', 'Released'];
  const budgetRanges = ['Under $10K', '$10K-$50K', '$50K-$250K', '$250K-$1M', '$1M+'];

  const watchedFields = watch();

  // Filter companies based on search
  const filteredCompanies = companies.filter(company => 
    company.toLowerCase().includes(companySearch.toLowerCase())
  );

  const handleCustomCompany = () => {
    if (companySearch.trim()) {
      addCompany(companySearch.trim());
      setValue('company', companySearch.trim());
      setShowCustomCompany(false);
      setCompanySearch('');
    }
  };

  const onSubmit = (data) => {
    const profile = {
      ...data,
      userId: user.id,
      email: user.email,
    };
    
    saveProfile(profile);
    navigate('/dashboard');
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleArrayField = (fieldName, value) => {
    const currentValues = watchedFields[fieldName] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    setValue(fieldName, newValues);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row gap-6">
              <div className="flex-1 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    {...register('name', { required: 'Name is required' })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Enter your full name"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company
                  </label>
                  {!showCustomCompany ? (
                    <div className="space-y-2">
                      <select
                        {...register('company')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        onChange={(e) => {
                          if (e.target.value === 'custom') {
                            setShowCustomCompany(true);
                          } else {
                            setValue('company', e.target.value);
                          }
                        }}
                      >
                        <option value="">Select your company</option>
                        {companies.map(company => (
                          <option key={company} value={company}>{company}</option>
                        ))}
                        <option value="custom">+ Add New Company</option>
                      </select>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={companySearch}
                          onChange={(e) => setCompanySearch(e.target.value)}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          placeholder="Enter company name"
                        />
                        <button
                          type="button"
                          onClick={handleCustomCompany}
                          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                        >
                          <SafeIcon icon={FiPlus} className="w-4 h-4" />
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setShowCustomCompany(false);
                          setCompanySearch('');
                        }}
                        className="text-sm text-gray-600 hover:text-gray-800"
                      >
                        ← Back to company list
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website
                  </label>
                  <input
                    {...register('website')}
                    type="url"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="https://your-website.com"
                  />
                </div>
              </div>
              
              {/* Logo Upload */}
              <div className="sm:w-40">
                <ImageUpload
                  label="Company Logo / Photo"
                  value={watchedFields.logo}
                  onChange={(value) => setValue('logo', value)}
                />
              </div>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Primary Role *
              </label>
              <div className="grid grid-cols-2 gap-3">
                {roles.map((role) => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => setValue('role', role)}
                    className={`p-4 border rounded-lg text-left transition-colors ${
                      watchedFields.role === role
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {role}
                  </button>
                ))}
              </div>
              {errors.role && <p className="text-red-500 text-sm mt-1">{errors.role.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Business Type
              </label>
              <div className="grid grid-cols-2 gap-3">
                {businessTypes.map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setValue('businessType', type)}
                    className={`p-3 border rounded-lg text-left text-sm transition-colors ${
                      watchedFields.businessType === type
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Platforms
              </label>
              <div className="grid grid-cols-3 gap-3">
                {platforms.map((platform) => (
                  <button
                    key={platform}
                    type="button"
                    onClick={() => handleArrayField('platforms', platform)}
                    className={`p-3 border rounded-lg text-center text-sm transition-colors ${
                      (watchedFields.platforms || []).includes(platform)
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {platform}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Genres
              </label>
              <div className="grid grid-cols-3 gap-3">
                {genres.map((genre) => (
                  <button
                    key={genre}
                    type="button"
                    onClick={() => handleArrayField('genres', genre)}
                    className={`p-3 border rounded-lg text-center text-sm transition-colors ${
                      (watchedFields.genres || []).includes(genre)
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {genre}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Target Markets
              </label>
              <div className="grid grid-cols-2 gap-3">
                {markets.map((market) => (
                  <button
                    key={market}
                    type="button"
                    onClick={() => handleArrayField('markets', market)}
                    className={`p-3 border rounded-lg text-center text-sm transition-colors ${
                      (watchedFields.markets || []).includes(market)
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {market}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="bg-indigo-50 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-semibold text-indigo-900 mb-2">Matching Preferences</h3>
              <p className="text-sm text-indigo-700">
                Help us find the best matches for you by specifying what you're looking for in potential partners.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Preferred Partner Roles
              </label>
              <div className="grid grid-cols-2 gap-3">
                {roles.map((role) => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => handleArrayField('preferredPartnerRoles', role)}
                    className={`p-3 border rounded-lg text-center text-sm transition-colors ${
                      (watchedFields.preferredPartnerRoles || []).includes(role)
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {role}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Preferred Experience Level
              </label>
              <div className="grid grid-cols-2 gap-3">
                {experienceLevels.map((level) => (
                  <button
                    key={level}
                    type="button"
                    onClick={() => handleArrayField('preferredExperienceLevel', level)}
                    className={`p-3 border rounded-lg text-center text-sm transition-colors ${
                      (watchedFields.preferredExperienceLevel || []).includes(level)
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Preferred Company Size
              </label>
              <div className="grid grid-cols-3 gap-3">
                {companySizes.map((size) => (
                  <button
                    key={size}
                    type="button"
                    onClick={() => handleArrayField('preferredCompanySize', size)}
                    className={`p-3 border rounded-lg text-center text-sm transition-colors ${
                      (watchedFields.preferredCompanySize || []).includes(size)
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Project Stage Interest
              </label>
              <div className="grid grid-cols-3 gap-3">
                {projectStages.map((stage) => (
                  <button
                    key={stage}
                    type="button"
                    onClick={() => handleArrayField('preferredProjectStages', stage)}
                    className={`p-3 border rounded-lg text-center text-sm transition-colors ${
                      (watchedFields.preferredProjectStages || []).includes(stage)
                        ? 'border-orange-500 bg-orange-50 text-orange-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {stage}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Budget Range (if applicable)
              </label>
              <div className="grid grid-cols-3 gap-3">
                {budgetRanges.map((range) => (
                  <button
                    key={range}
                    type="button"
                    onClick={() => setValue('preferredBudgetRange', range)}
                    className={`p-3 border rounded-lg text-center text-sm transition-colors ${
                      watchedFields.preferredBudgetRange === range
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {range}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                What do you need?
              </label>
              <div className="grid grid-cols-2 gap-3">
                {needsOffers.map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => handleArrayField('needs', item)}
                    className={`p-3 border rounded-lg text-center text-sm transition-colors ${
                      (watchedFields.needs || []).includes(item)
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                What can you offer?
              </label>
              <div className="grid grid-cols-2 gap-3">
                {needsOffers.map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => handleArrayField('offers', item)}
                    className={`p-3 border rounded-lg text-center text-sm transition-colors ${
                      (watchedFields.offers || []).includes(item)
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Summary
              </label>
              <textarea
                {...register('summary')}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Tell us about yourself, your goals, and what makes you unique..."
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-xl shadow-sm p-8">
          {/* Progress */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              {steps.map((step, index) => (
                <div key={step} className="flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      index <= currentStep
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {index < currentStep ? (
                      <SafeIcon icon={FiCheck} className="w-5 h-5" />
                    ) : (
                      index + 1
                    )}
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`w-16 h-1 mx-2 ${
                        index < currentStep ? 'bg-indigo-600' : 'bg-gray-200'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-600">
              Step {currentStep + 1} of {steps.length}: {steps[currentStep]}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)}>
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="mb-8"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {steps[currentStep]}
              </h2>
              {renderStep()}
            </motion.div>

            {/* Navigation */}
            <div className="flex justify-between">
              <button
                type="button"
                onClick={prevStep}
                disabled={currentStep === 0}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center space-x-2"
              >
                <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
                <span>Previous</span>
              </button>

              {currentStep === steps.length - 1 ? (
                <button
                  type="submit"
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors inline-flex items-center space-x-2"
                >
                  <span>Complete Profile</span>
                  <SafeIcon icon={FiCheck} className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={nextStep}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors inline-flex items-center space-x-2"
                >
                  <span>Next</span>
                  <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfileWizard;